import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../components/ui/hover-card"
import { cn } from "../lib/utils"

export function HoverCardRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain.
  const atomDefaults = useAtomDefaults("hover-card")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const children = element.children ?? []
  const trigger = children[0]
  const content = children[1]

  // defaultOpen: force-open uncontrolled state (preview / initial-open use cases).
  // Matches the pattern already established on alert-dialog-renderer.tsx,
  // dialog-renderer.tsx, popover-renderer.tsx, and tooltip-renderer.tsx.
  // base-ui's PreviewCardRoot supports `defaultOpen` natively.
  const defaultOpen = (element as unknown as Record<string, unknown>).defaultOpen === true

  return (
    <span
      data-atom-type="hover-card"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-testid={`hover-card-wrapper-${element.id}`}
    >
      <HoverCard defaultOpen={defaultOpen}>
        {trigger && (
          <HoverCardTrigger
            className={cn(element.className)}
            data-testid={`hover-card-${element.id}`}
            render={<span />}
          >
            <ElementRenderer element={trigger} />
          </HoverCardTrigger>
        )}
        {content && (
          <HoverCardContent>
            <ElementRenderer element={content} />
          </HoverCardContent>
        )}
      </HoverCard>
    </span>
  )
}
